import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

// Fitness Assessment V2 · Phase 3 — Admin-only legacy backfill card.
//
// Dry-run first (GET /api/assessments/backfill/preview). Apply is
// hidden until preview has been run and shows how many rows would be
// updated. Apply uses a standard confirm modal (never overwrites
// already-classified rows).

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatRow = ({ label, value, tone = 'default' }) => {
  const toneCls = {
    default: 'text-gray-700 dark:text-gray-300',
    good: 'text-emerald-700 dark:text-emerald-300',
    warn: 'text-amber-700 dark:text-amber-300',
    mute: 'text-gray-500 dark:text-gray-500',
  }[tone];
  return (
    <div className="flex justify-between text-sm py-1">
      <span className={toneCls}>{label}</span>
      <span className={`font-semibold ${toneCls}`}>{value}</span>
    </div>
  );
};

export const BackfillPreviewCard = () => {
  const [preview, setPreview] = useState(null);
  const [applyReport, setApplyReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const runPreview = async () => {
    setLoading(true);
    setApplyReport(null);
    try {
      const res = await axios.get(`${API}/assessments/backfill/preview`);
      setPreview(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to run preview');
    } finally {
      setLoading(false);
    }
  };

  const runApply = async () => {
    setApplying(true);
    try {
      const res = await axios.post(`${API}/assessments/backfill/apply`);
      setApplyReport(res.data);
      toast.success(`Backfill applied — updated ${res.data.updated} row(s)`);
      setPreview(null); // Re-run preview to reflect new state.
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to apply backfill');
    } finally {
      setApplying(false);
      setConfirmOpen(false);
    }
  };

  return (
    <Card className="mb-6" data-testid="backfill-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <span>Legacy Classification Backfill</span>
          <Badge variant="outline" className="text-[10px]">Admin only</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Dry-run preview shows how many legacy results the Phase 2
          standards engine would classify if re-run. Apply writes only
          rows that are currently unclassified — it never overwrites
          existing classifications.
        </p>

        <div className="flex gap-2 mb-4">
          <Button
            onClick={runPreview}
            disabled={loading}
            variant="outline"
            data-testid="backfill-preview-btn"
          >
            {loading ? 'Running preview…' : 'Run dry-run preview'}
          </Button>
          <Button
            onClick={() => setConfirmOpen(true)}
            disabled={!preview || preview.would_classify === 0 || applying}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="backfill-apply-btn"
          >
            {applying ? 'Applying…' : 'Apply backfill'}
          </Button>
        </div>

        {preview && (
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900/40"
               data-testid="backfill-preview-report">
            <p className="text-sm font-medium mb-3">Preview report</p>
            <StatRow label="Total scanned" value={preview.total_scanned} />
            <StatRow label="Would classify" value={preview.would_classify} tone="good" />
            <StatRow label="Skipped · already classified" value={preview.skipped_already_classified} tone="mute" />
            <StatRow label="Skipped · missing DOB" value={preview.missing_dob} tone="warn" />
            <StatRow label="Skipped · missing gender" value={preview.missing_gender} tone="warn" />
            <StatRow label="Skipped · no matching test" value={preview.missing_test_mapping} tone="warn" />
            <StatRow label="Skipped · age out of range" value={preview.age_out_of_range} tone="warn" />
            <StatRow label="Skipped · invalid value" value={preview.invalid_value} tone="warn" />

            {preview.sample_would_classify?.length > 0 && (
              <div className="mt-3">
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Sample (up to 5)</p>
                <ul className="text-xs space-y-1">
                  {preview.sample_would_classify.map((s, i) => (
                    <li key={i} className="text-gray-600 dark:text-gray-300">
                      • {s.test_type} · {s.value} → <strong>{s.would_become}</strong>
                      {s.season ? ` · Season ${s.season}` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {applyReport && (
          <div className="mt-4 rounded-lg border border-emerald-200 dark:border-emerald-800 p-4 bg-emerald-50 dark:bg-emerald-900/20"
               data-testid="backfill-apply-report">
            <p className="text-sm font-medium text-emerald-800 dark:text-emerald-200 mb-2">
              Apply complete
            </p>
            <StatRow label="Total scanned" value={applyReport.total_scanned} />
            <StatRow label="Updated" value={applyReport.updated} tone="good" />
            <StatRow label="Skipped · already classified" value={applyReport.skipped_already_classified} tone="mute" />
          </div>
        )}
      </CardContent>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="backfill-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Apply backfill?</AlertDialogTitle>
            <AlertDialogDescription>
              This will classify {preview?.would_classify ?? 0} legacy
              result(s) using the current standards engine. Already-
              classified rows are skipped. This action writes to the
              database.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="backfill-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={runApply}
              disabled={applying}
              data-testid="backfill-confirm-apply"
            >
              Yes, apply
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default BackfillPreviewCard;
